import React, { useState, useEffect, useCallback } from 'react'
import { franchiseService } from '../../services/franchise.service'
import { formatGb, formatGbPair, hasGbValue, formatNumber, usageStatus, eventsUsageStatus, projectEventsUsage, RESOURCE_COLORS } from '../../utils/franchiseFormat'
import StackedUsageBar from './StackedUsageBar'
import { ValueSlot, BarSlot } from './UsageSlots'
import FranchiseUsageModal from './modals/FranchiseUsageModal'

const MiniMeter = ({ label, icon, valueLabel, percentage, busy, hasError, projectionTone }) => {
  const status = eventsUsageStatus(percentage, projectionTone)
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] leading-none mb-1">
        <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
          <span className="material-icons" style={{ fontSize: 12 }}>{icon}</span>
          {label}
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`font-semibold tabular-nums ${busy || hasError ? '' : status.text}`}>
            <ValueSlot busy={busy} hasError={hasError} width="w-7">{Math.round(percentage)}%</ValueSlot>
          </span>
          <span className="text-gray-400 dark:text-gray-500 tabular-nums">
            <ValueSlot busy={busy} hasError={hasError} width="w-14">{valueLabel}</ValueSlot>
          </span>
        </span>
      </div>
      <BarSlot busy={busy} hasError={hasError} heightClass="h-1">
        <div className={`h-1 rounded-full overflow-hidden ${status.track}`}>
          <div
            className={`h-full rounded-full ${status.bar} transition-all duration-500`}
            style={{ width: `${Math.min(Math.max(percentage, 2), 100)}%` }}
          />
        </div>
      </BarSlot>
    </div>
  )
}

const FranchiseUsageCard = ({ apiToken }) => {
  const [usage, setUsage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const load = useCallback(async () => {
    if (!apiToken) return
    setLoading(true)
    setError(false)
    try {
      const data = await franchiseService.getUsage(apiToken)
      setUsage(data)
    } catch (err) {
      console.error('Error loading franchise usage:', err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [apiToken])

  useEffect(() => { load() }, [load])

  const busy = loading
  const hasError = !loading && error && !usage

  const dbGb = usage ? usage.database.used_gb : 0
  const storageGb = usage ? usage.storage.used_gb : 0
  const limitGb = usage ? usage.limit_gb : 0
  const diskPercentage = usage && usage.limit_gb
    ? Math.min(((dbGb + storageGb) / usage.limit_gb) * 100, 100)
    : 0
  const diskStatus = usageStatus(diskPercentage)
  // Hide a resource that would render as "0.00 GB"; keep both while loading/on
  // error (placeholders) and when both are empty so the row never vanishes.
  const bothEmpty = !hasGbValue(dbGb) && !hasGbValue(storageGb)
  const showDb = busy || hasError || bothEmpty || hasGbValue(dbGb)
  const showStorage = busy || hasError || bothEmpty || hasGbValue(storageGb)
  const eventsProjection = usage && !busy && !hasError ? projectEventsUsage(usage.events) : null

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/40 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
          <span className="material-icons" style={{ fontSize: 12 }}>workspace_premium</span>
          Franquia
          {hasError && (
            <span
              className="material-icons text-red-500 dark:text-red-400"
              style={{ fontSize: 12 }}
              title="Não foi possível carregar os dados da franquia"
            >
              error_outline
            </span>
          )}
        </span>
        <button
          onClick={load}
          disabled={loading}
          title="Atualizar franquia"
          className="p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-gray-700/60 transition disabled:opacity-40"
        >
          <span className={`material-icons ${loading ? 'animate-spin' : ''}`} style={{ fontSize: 12 }}>refresh</span>
        </button>
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] leading-none mb-1">
          <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
            <span className="material-icons" style={{ fontSize: 12 }}>data_usage</span>
            Armazenamento
          </span>
          <span className="flex items-center gap-1.5">
            <span className={`font-semibold tabular-nums ${busy || hasError ? '' : diskStatus.text}`}>
              <ValueSlot busy={busy} hasError={hasError} width="w-7">{Math.round(diskPercentage)}%</ValueSlot>
            </span>
            <span className="text-gray-400 dark:text-gray-500 tabular-nums">
              <ValueSlot busy={busy} hasError={hasError} width="w-16">{formatGbPair(dbGb + storageGb, limitGb)}</ValueSlot>
            </span>
          </span>
        </div>
        <BarSlot busy={busy} hasError={hasError} heightClass="h-1">
          <StackedUsageBar
            heightClass="h-1"
            total={limitGb}
            segments={[
              { value: dbGb, colorClass: RESOURCE_COLORS.database.bar },
              { value: storageGb, colorClass: RESOURCE_COLORS.storage.bar },
            ]}
          />
        </BarSlot>
        <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400 dark:text-gray-500">
          {showDb && (
            <span className="flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${RESOURCE_COLORS.database.dot}`} />
              Banco <ValueSlot busy={busy} hasError={hasError} width="w-10">{formatGb(dbGb)}</ValueSlot>
            </span>
          )}
          {showStorage && (
            <span className="flex items-center gap-1">
              <span className={`w-1.5 h-1.5 rounded-full ${RESOURCE_COLORS.storage.dot}`} />
              Arquivos <ValueSlot busy={busy} hasError={hasError} width="w-10">{formatGb(storageGb)}</ValueSlot>
            </span>
          )}
        </div>
      </div>

      <MiniMeter
        label="Eventos"
        icon="event_repeat"
        busy={busy}
        hasError={hasError}
        valueLabel={usage ? `${formatNumber(usage.events.used)} / ${formatNumber(usage.events.limit)}` : ''}
        percentage={usage ? usage.events.percentage_used : 0}
        projectionTone={eventsProjection?.tone}
      />

      <button
        onClick={() => setModalOpen(true)}
        disabled={loading}
        className="w-full flex items-center justify-center gap-1 py-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-40 transition-colors"
      >
        Ver detalhes
        <span className="material-icons" style={{ fontSize: 12 }}>chevron_right</span>
      </button>

      <FranchiseUsageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        usage={usage}
        loading={loading}
        onRefresh={load}
      />
    </div>
  )
}

export default FranchiseUsageCard
